import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShoppingCart, Plus, Minus, X, Truck, Package, Check,
  ChevronLeft, Tag, Search, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'all', label: 'All Products' },
  { value: 'hats', label: 'Hats' },
  { value: 'shirts', label: 'Shirts' },
  { value: 'outerwear', label: 'Outerwear' },
  { value: 'gear', label: 'Gear' },
  { value: 'accessories', label: 'Accessories' },
];

const CA_PROVINCES = [
  { value: 'AB', label: 'Alberta' }, { value: 'BC', label: 'British Columbia' },
  { value: 'SK', label: 'Saskatchewan' }, { value: 'MB', label: 'Manitoba' },
  { value: 'ON', label: 'Ontario' }, { value: 'QC', label: 'Quebec' },
  { value: 'NB', label: 'New Brunswick' }, { value: 'NS', label: 'Nova Scotia' },
  { value: 'PE', label: 'PEI' }, { value: 'NL', label: 'Newfoundland' },
  { value: 'NT', label: 'NWT' }, { value: 'NU', label: 'Nunavut' },
  { value: 'YT', label: 'Yukon' }, { value: 'US', label: 'United States' },
];

export default function StorePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ea_cart') || '[]'); } catch { return []; }
  });
  const [showCart, setShowCart] = useState(false);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [shippingRegion, setShippingRegion] = useState('AB');
  const [shippingCost, setShippingCost] = useState(0);
  const [shippingNote, setShippingNote] = useState('');
  const [checkingOut, setCheckingOut] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);

  // Persist cart to localStorage
  useEffect(() => {
    localStorage.setItem('ea_cart', JSON.stringify(cart));
  }, [cart]);

  // Check for order completion
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setOrderComplete(true);
      setCart([]);
      localStorage.removeItem('ea_cart');
    }
  }, [searchParams]);

  const fetchProducts = useCallback(async () => {
    try {
      const params = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
      const { data } = await axios.get(`${API_URL}/api/store/products${params}`);
      setProducts(data.products);
    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const calculateShipping = useCallback(async () => {
    const subtotal = cart.reduce((sum, i) => sum + i.price * i.quantity, 0);
    try {
      const { data } = await axios.post(`${API_URL}/api/store/calculate-shipping`, {
        region: shippingRegion, subtotal
      });
      setShippingCost(data.shipping);
      setShippingNote(data.note);
    } catch { /* fallback */ }
  }, [shippingRegion, cart]);

  useEffect(() => { if (cart.length > 0) calculateShipping(); }, [calculateShipping, cart.length]);

  const addToCart = (product, size, color) => {
    const existing = cart.find(i =>
      i.product_id === product.id && i.size === size && i.color === color
    );
    if (existing) {
      setCart(cart.map(i =>
        i === existing ? { ...i, quantity: i.quantity + 1 } : i
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        image: product.images?.[0] || '',
        size, color, quantity: 1
      }]);
    }
    toast.success(`${product.name} added to cart`);
    setSelectedProduct(null);
    setSelectedSize('');
    setSelectedColor('');
  };

  const updateQuantity = (index, delta) => {
    setCart(cart.map((item, i) => {
      if (i !== index) return item;
      const newQty = item.quantity + delta;
      return newQty > 0 ? { ...item, quantity: newQty } : null;
    }).filter(Boolean));
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const subtotal = cart.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const total = subtotal + shippingCost;

  const handleCheckout = async () => {
    const token = localStorage.getItem('token');
    if (!token) { toast.error('Please log in to checkout'); navigate('/login'); return; }
    setCheckingOut(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/store/checkout`, {
        items: cart,
        shipping_region: shippingRegion,
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
    } finally {
      setCheckingOut(false);
    }
  };

  const filteredProducts = products.filter(p =>
    searchTerm ? p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchTerm.toLowerCase()) : true
  );

  const getSizes = (product) => [...new Set((product.variants || []).map(v => v.size).filter(Boolean))];
  const getColors = (product, size) => {
    return [...new Set((product.variants || []).filter(v => !size || v.size === size).map(v => v.color).filter(Boolean))];
  };
  const getStock = (product, size, color) => {
    const v = (product.variants || []).find(v => v.size === size && v.color === color);
    return v ? v.stock : 0;
  };

  // Order complete screen
  if (orderComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-display text-3xl font-bold uppercase mb-3">Order Confirmed!</h1>
          <p className="text-muted-foreground mb-6">
            Thank you for your purchase! You'll receive a confirmation email shortly.
            Your order will be shipped from Taber, Alberta.
          </p>
          <Button onClick={() => { setOrderComplete(false); navigate('/store'); }} data-testid="continue-shopping-btn">
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  // Product detail view
  if (selectedProduct) {
    const sizes = getSizes(selectedProduct);
    const colors = getColors(selectedProduct, selectedSize);
    const stock = selectedSize && selectedColor ? getStock(selectedProduct, selectedSize, selectedColor) : null;

    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => setSelectedProduct(null)} className="mb-6" data-testid="back-to-store-btn">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to Store
        </Button>
        <div className="grid md:grid-cols-2 gap-8">
          {/* Images */}
          <div className="space-y-3">
            {selectedProduct.images?.length > 0 ? (
              <img src={selectedProduct.images[0]} alt={selectedProduct.name}
                className="w-full aspect-square object-cover rounded-lg" data-testid="product-main-image" />
            ) : (
              <div className="w-full aspect-square bg-muted rounded-lg flex items-center justify-center">
                <Package className="w-16 h-16 text-muted-foreground" />
              </div>
            )}
            {selectedProduct.images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {selectedProduct.images.map((img, i) => (
                  <img key={i} src={img} alt="" className="w-20 h-20 object-cover rounded cursor-pointer border-2 border-transparent hover:border-primary" />
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-5">
            <div>
              <Badge variant="outline" className="mb-2">{selectedProduct.category}</Badge>
              <h1 className="font-display text-3xl font-bold uppercase" data-testid="product-name">{selectedProduct.name}</h1>
              <p className="text-3xl font-bold text-primary mt-2" data-testid="product-price">${selectedProduct.price.toFixed(2)} CAD</p>
            </div>
            {selectedProduct.description && (
              <p className="text-muted-foreground">{selectedProduct.description}</p>
            )}

            {/* Size Selection */}
            {sizes.length > 0 && (
              <div>
                <label className="text-sm font-medium mb-2 block">Size</label>
                <div className="flex flex-wrap gap-2">
                  {sizes.map(size => (
                    <Button key={size} variant={selectedSize === size ? 'default' : 'outline'} size="sm"
                      onClick={() => { setSelectedSize(size); setSelectedColor(''); }}
                      data-testid={`size-${size}`}>
                      {size}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Selection */}
            {colors.length > 0 && (
              <div>
                <label className="text-sm font-medium mb-2 block">Color</label>
                <div className="flex flex-wrap gap-2">
                  {colors.map(color => (
                    <Button key={color} variant={selectedColor === color ? 'default' : 'outline'} size="sm"
                      onClick={() => setSelectedColor(color)}
                      data-testid={`color-${color}`}>
                      {color}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Stock */}
            {stock !== null && (
              <p className={`text-sm ${stock > 0 ? 'text-green-600' : 'text-red-500'}`} data-testid="stock-status">
                {stock > 0 ? `${stock} in stock` : 'Out of stock'}
              </p>
            )}

            {/* Add to Cart */}
            <Button size="lg" className="w-full"
              disabled={(sizes.length > 0 && !selectedSize) || (colors.length > 0 && !selectedColor) || stock === 0}
              onClick={() => addToCart(selectedProduct, selectedSize, selectedColor)}
              data-testid="add-to-cart-btn">
              <ShoppingCart className="w-5 h-5 mr-2" />
              Add to Cart — ${selectedProduct.price.toFixed(2)} CAD
            </Button>

            {/* Shipping info */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground border-t pt-4">
              <Truck className="w-4 h-4" />
              <span>Ships from Taber, Alberta. Free shipping over $150 CAD.</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl font-bold uppercase" data-testid="store-heading">Store</h1>
          <p className="text-muted-foreground mt-1">Official Elevated Arena Merchandise</p>
        </div>
        <Button variant="outline" className="relative" onClick={() => setShowCart(true)} data-testid="cart-btn">
          <ShoppingCart className="w-5 h-5 mr-2" /> Cart
          {cart.length > 0 && (
            <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground rounded-full w-5 h-5 text-xs flex items-center justify-center">
              {cart.reduce((s, i) => s + i.quantity, 0)}
            </span>
          )}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search products..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="pl-9" data-testid="store-search" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {CATEGORIES.map(cat => (
            <Button key={cat.value} variant={selectedCategory === cat.value ? 'default' : 'outline'} size="sm"
              onClick={() => setSelectedCategory(cat.value)} className="whitespace-nowrap"
              data-testid={`cat-${cat.value}`}>
              {cat.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-20">
          <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-display text-lg font-bold uppercase">No Products Yet</h3>
          <p className="text-muted-foreground mt-1">Check back soon for new merchandise!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {filteredProducts.map(product => (
            <Card key={product.id} className="cursor-pointer group overflow-hidden hover:shadow-lg transition-shadow"
              onClick={() => setSelectedProduct(product)} data-testid={`product-${product.id}`}>
              <div className="aspect-square overflow-hidden bg-muted">
                {product.images?.[0] ? (
                  <img src={product.images[0]} alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-10 h-10 text-muted-foreground" />
                  </div>
                )}
              </div>
              <CardContent className="p-3 sm:p-4">
                <p className="font-display font-bold uppercase text-sm truncate">{product.name}</p>
                <p className="text-primary font-bold mt-1">${product.price.toFixed(2)} CAD</p>
                {product.variants?.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {[...new Set(product.variants.map(v => v.size).filter(Boolean))].length} sizes available
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="cart-sidebar">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCart(false)} />
          <div className="relative w-full max-w-md bg-background shadow-xl flex flex-col h-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="font-display font-bold text-xl uppercase">Your Cart</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowCart(false)} data-testid="close-cart-btn">
                <X className="w-5 h-5" />
              </Button>
            </div>

            {cart.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <ShoppingCart className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">Your cart is empty</p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {cart.map((item, i) => (
                    <div key={i} className="flex gap-3 border rounded-lg p-3" data-testid={`cart-item-${i}`}>
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="w-16 h-16 rounded object-cover" />
                      ) : (
                        <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
                          <Package className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm truncate">{item.name}</p>
                        {(item.size || item.color) && (
                          <p className="text-xs text-muted-foreground">
                            {[item.size, item.color].filter(Boolean).join(' / ')}
                          </p>
                        )}
                        <p className="text-sm font-bold mt-1">${(item.price * item.quantity).toFixed(2)}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Button variant="outline" size="icon" className="h-6 w-6"
                            onClick={() => updateQuantity(i, -1)} data-testid={`qty-minus-${i}`}>
                            <Minus className="w-3 h-3" />
                          </Button>
                          <span className="text-sm w-6 text-center">{item.quantity}</span>
                          <Button variant="outline" size="icon" className="h-6 w-6"
                            onClick={() => updateQuantity(i, 1)} data-testid={`qty-plus-${i}`}>
                            <Plus className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6 ml-auto text-destructive"
                            onClick={() => removeFromCart(i)} data-testid={`remove-item-${i}`}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Shipping & Total */}
                <div className="border-t p-4 space-y-3">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Ship to</label>
                    <Select value={shippingRegion} onValueChange={setShippingRegion}>
                      <SelectTrigger data-testid="shipping-region-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CA_PROVINCES.map(p => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Subtotal</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="flex items-center gap-1">
                        <Truck className="w-3 h-3" /> Shipping
                      </span>
                      <span>{shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}</span>
                    </div>
                    {shippingNote && <p className="text-xs text-green-600">{shippingNote}</p>}
                    <div className="flex justify-between font-bold text-base border-t pt-2">
                      <span>Total</span>
                      <span>${total.toFixed(2)} CAD</span>
                    </div>
                  </div>
                  <Button className="w-full" size="lg" onClick={handleCheckout} disabled={checkingOut}
                    data-testid="checkout-btn">
                    {checkingOut ? 'Processing...' : `Checkout — $${total.toFixed(2)} CAD`}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
